import React, { useCallback } from "react";
import { useProjectContext } from "../contexts/ProjectContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useActionSnackbar } from '../contexts/ActionSnackbarContext'; // Import Action Snackbar
import { useAuth } from "./useAuth";
import { useIntegrations } from "./useIntegrations";
import useStore, { FlowState } from "../store/useStore"; // Import FlowState directly
import { projectService } from "../services/project.service";
import { templateService } from "../services/template.service";
import { reportService } from "../services/report.service";
import { Project, Node as FlowNode, Edge as FlowEdge } from "../types";
import { saveProjectId } from "../store/persistence";
import { hasUnsavedChanges } from "../utils/projectUtils";
import {
  readFileAsText,
  findIntegrationForUrl,
  suggestConsentTool,
  generateNodeId,
  generateEdgeId,
  formatFileSize,
  parseCsvLine,
  ScriptInfo,
  IdentityInfo,
  DEFAULT_CLUSTER_NAME,
  CLUSTER_SETTINGS,
  CLUSTER_TEAM_ACCESS,
} from "../utils/csvImportUtils";

// Define the props type for the hook
interface UseProjectMenuHandlersProps {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAuthRequiredModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSaveAsDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setShowTemplateMenu: React.Dispatch<React.SetStateAction<boolean>>;
  setShowNewProjectConfirmDialog: React.Dispatch<React.SetStateAction<boolean>>; // Added setter for confirm dialog
  fileInputRef: React.RefObject<HTMLInputElement>;
  csvFileInputRef: React.RefObject<HTMLInputElement>;
  setNodes: FlowState['setNodes']; // Use FlowState for setNodes prop type
  setEdges: FlowState['setEdges']; // Use FlowState for setEdges prop type
}

export function useProjectMenuHandlers({
  setIsOpen,
  setShowAuthRequiredModal,
  setShowSaveAsDialog,
  setShowTemplateMenu,
  setShowNewProjectConfirmDialog, // Added setter
  fileInputRef,
  csvFileInputRef,
  setNodes, // Destructure setNodes
  setEdges, // Destructure setEdges
}: UseProjectMenuHandlersProps) {
  const { project, saveProject, loadProject, setProject } = useProjectContext();
  const { user, requiresAuth } = useAuth();
  const { showNotification } = useNotifications();
  const { showSnackbar } = useActionSnackbar(); // Get showSnackbar (Corrected name)
  const { integrations, loadIntegrations } = useIntegrations(); // Get loadIntegrations as well
  // setNodes and setEdges are now passed in as props

  // --- Handler Implementations (Copied and adapted from ProjectMenu.tsx) ---

  // Placeholder for Save As logic - opens the dialog
  const handleSaveAs = useCallback(() => {
    if (requiresAuth('save')) {
      setShowAuthRequiredModal(true);
      setIsOpen(false);
      return;
    }
    setShowSaveAsDialog(true); // Open the Save As dialog
    setIsOpen(false); // Close the menu
  }, [requiresAuth, setShowAuthRequiredModal, setShowSaveAsDialog, setIsOpen]);

  const handleSave = useCallback(async () => {
    if (requiresAuth('save')) {
      setShowAuthRequiredModal(true);
      setIsOpen(false); // Close menu if auth required
      return;
    }

    // If it's a new, unsaved project, trigger "Save As" instead
    if (project?.id === 'unsaved-project') {
      handleSaveAs();
      return;
    }

    // Otherwise, proceed with the normal save
    setIsOpen(false); // Close the menu immediately
    showSnackbar("Saving project..."); // Show saving message
    try {
      await saveProject();
      // Snackbar will update on success/failure via saveProject's internal logic
    } catch (error) {
      // Error is handled within saveProject, which calls showSnackbar
      console.error("Save failed in handleSave:", error);
    }
  }, [requiresAuth, setShowAuthRequiredModal, setIsOpen, saveProject, showSnackbar, project, handleSaveAs]); // Added project and handleSaveAs



  // --- New Project Logic with Confirmation ---

  // Original logic to create a new project (clears state)
  const proceedWithNewProject = useCallback(() => {
    setNodes([]);
    setEdges([]);
    const emptyProject = new Project({
      id: 'unsaved-project', name: 'Untitled Project', nodes: [], edges: [], owner_id: user?.id || '',
      created_at: new Date().toISOString(), updated_at: new Date().toISOString(), shared_with: [], is_locked: false,
      is_archived: false, version: 1, user_id: user?.id, settings: { preview: null },
    });
    setProject(emptyProject);
    useStore.getState().setProject(emptyProject);
    saveProjectId(null);
    reportService.clearReportFromLocalStorage();
    localStorage.removeItem('mr-architect-flow-state'); // Clear flow state from local storage
    showNotification("success", "New project created");
    setIsOpen(false);
  }, [setNodes, setEdges, user, setProject, showNotification, setIsOpen]);

  // Logic to save current project THEN create a new one
  const saveAndProceedWithNewProject = useCallback(async () => {
    try {
      // Attempt to save. handleSave will trigger Save As if needed.
      await handleSave(); 
      // Attempt to save. handleSave will trigger Save As if needed.
      await handleSave();
      // After the save attempt, check the project context *again*.
      // We access `project` from the hook's scope, assuming the context update
      // happened and triggered a re-render or the closure captured the updated value.
      if (project && project.id !== 'unsaved-project') {
         proceedWithNewProject(); // Only proceed if save was successful (project now has a real ID)
      } else {
         // Save might have failed (e.g., auth error, API error, or triggered Save As)
         // Notification/snackbar is handled within handleSave/handleSaveAs
         console.warn("Save operation did not complete successfully before creating new project.");
      }
    } catch (error) {
      // Error handled within handleSave
      console.error("Error during save operation before creating new project:", error);
    }
  }, [handleSave, proceedWithNewProject]);


  // Updated handleNewProject to check for unsaved changes
  const handleNewProject = useCallback(() => {
    setIsOpen(false); // Close menu immediately

    // Check for unsaved changes (requires access to saved state)
    const currentNodes = useStore.getState().nodes;
    const currentEdges = useStore.getState().edges;

    if (hasUnsavedChanges(project, currentNodes, currentEdges)) {
      setShowNewProjectConfirmDialog(true); // Show confirmation dialog
    } else {
      proceedWithNewProject(); // No unsaved changes, proceed directly
    }
  }, [project, setIsOpen, setShowNewProjectConfirmDialog, proceedWithNewProject]);

  // --- End New Project Logic ---


  const handleDelete = async () => {
    if (!project) return;
    try {
      await projectService.deleteProject(project.id);
      const emptyProject = new Project({
        id: 'unsaved-project', name: 'Untitled Project', nodes: [], edges: [], owner_id: user?.id || '',
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(), shared_with: [], is_locked: false,
        is_archived: false, version: 1, user_id: user?.id, settings: { preview: null },
      });
      setNodes([]); setEdges([]);
    setProject(emptyProject);
    useStore.getState().setProject(emptyProject);
    saveProjectId(null);
    reportService.clearReportFromLocalStorage();
    showNotification("success", "Project deleted successfully");
      setIsOpen(false);
    } catch (error) {
      console.error("Error deleting project:", error);
      if (error instanceof Error && error.message.includes('404')) {
        const emptyProject = new Project({
          id: 'unsaved-project', name: 'Untitled Project', nodes: [], edges: [], owner_id: user?.id || '',
          created_at: new Date().toISOString(), updated_at: new Date().toISOString(), shared_with: [], is_locked: false,
          is_archived: false, version: 1, user_id: user?.id, settings: { preview: null },
        });
        setNodes([]); setEdges([]);
    setProject(emptyProject);
    useStore.getState().setProject(emptyProject);
    saveProjectId(null);
    reportService.clearReportFromLocalStorage();
    showNotification("success", "Project deleted successfully");
        setIsOpen(false);
      } else {
        showNotification("error", "Failed to delete project");
      }
    }
  };

  // The erroneous duplicate code block was here - removing it.

  const handleTemplateSelect = useCallback(async (templateId: string) => {
    setIsOpen(false); // Close the main menu immediately
    setShowTemplateMenu(false); // Close the template submenu immediately
    showSnackbar("Loading template..."); // Show loading message
    try {
      const template = await templateService.getTemplate(templateId);
      setNodes(template.nodes);
      setEdges(template.edges);
      saveProjectId(null); // Clear saved ID when loading a template
      // No success message needed, snackbar shows "Loading..."

      // --- ADDED: Fit view after loading template ---
      setTimeout(() => {
        useStore.getState().reactFlowInstance?.fitView({ padding: 0.2, duration: 200 });
      }, 0);
      // --- END ADDED ---

    } catch (error) {
      console.error("Error loading template:", error);
      showSnackbar("Failed to load template"); // Show error message using snackbar
    }
  }, [setIsOpen, setShowTemplateMenu, showSnackbar, setNodes, setEdges]); // Dependencies remain the same, useStore is accessed via getState()

  const handleExportProject = async () => {
    if (!project) return;
    try {
      const projectData = await projectService.exportProject(project.id);
      if (!projectData) throw new Error("Received empty project data from server");
      const jsonData = JSON.stringify(projectData, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name.replace(/\s+/g, '_')}_export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showNotification("success", "Project exported successfully");
    } catch (error) {
      console.error("Error exporting project:", error);
      showNotification("error", "Failed to export project");
    }
  };

  const handleImportProject = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImportAuditCsv = () => {
    console.log("[ProjectMenuHandlers] handleImportAuditCsv called");
    if (csvFileInputRef.current) {
      console.log("[ProjectMenuHandlers] Triggering CSV file input click");
      csvFileInputRef.current.click();
    } else {
      console.error("[ProjectMenuHandlers] CSV file input ref not found!");
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const fileContent = await readFileAsText(file);
      const projectData = JSON.parse(fileContent);
      if (!projectData.name || !Array.isArray(projectData.nodes) || !Array.isArray(projectData.edges)) {
        showNotification("error", "Invalid project file format");
        return;
      }
      const importedProject = await projectService.importProject({
        name: projectData.name,
        description: projectData.description,
        nodes: projectData.nodes,
        edges: projectData.edges
      });
      await loadProject(importedProject.id);
      showNotification("success", "Project imported successfully");
    } catch (error) {
      console.error("Error importing project:", error);
      showNotification("error", "Failed to import project");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("[ProjectMenuHandlers] handleCsvFileChange called");
    const file = event.target.files?.[0];
    if (!file) {
      console.log("[ProjectMenuHandlers] No file selected");
      return;
    }
    console.log("[ProjectMenuHandlers] File selected:", file.name);

    try {
        const csvContent = await readFileAsText(file);
        console.log("[ProjectMenuHandlers] CSV content read");
        const lines = csvContent.split('\n').map(line => line.trim());

        let websiteUrl = '';
        const scripts: ScriptInfo[] = [];
        const identities: IdentityInfo[] = [];
        let parsingSection: 'header' | 'scripts' | 'identities' | 'none' = 'none';

        for (const line of lines) {
             if (!line) {
                continue;
            }
            const parts = parseCsvLine(line);

            if (line.startsWith('Website URL:')) {
                const contentAfterLabel = line.substring('Website URL:'.length).trim();
                const httpIndex = contentAfterLabel.indexOf('http');
                if (httpIndex !== -1) {
                    websiteUrl = contentAfterLabel.substring(httpIndex).trim();
                } else {
                    const urlParts = contentAfterLabel.split(',');
                    if (urlParts.length > 1) {
                        websiteUrl = urlParts[1].trim();
                    }
                }
                console.log(`[ProjectMenuHandlers] Extracted Website URL: ${websiteUrl}`);
                parsingSection = 'header';
            } else if (line.startsWith('Script Audit')) {
                parsingSection = 'scripts';
                console.log(`[ProjectMenuHandlers] Switched to parsingSection = 'scripts'`);
            } else if (line.startsWith('Identities')) {
                console.log(`[ProjectMenuHandlers] Found Identities section, stopping script parsing.`);
                parsingSection = 'identities';
            } else if (parsingSection === 'scripts') {
                 console.log(`[ProjectMenuHandlers] In 'scripts' section. Processing line: "${line}"`);
                 if (parts[0] === 'URL' && parts[1] === 'File Size (kB)' && parts[2] === 'Load Time (ms)') {
                    console.log(`[ProjectMenuHandlers] Identified script header row.`);
                 } else if (parts.length >= 3) {
                    console.log(`[ProjectMenuHandlers] Attempting to parse as script data line.`);
                    console.log(`[ProjectMenuHandlers] Parsed parts:`, parts);
                    const url = parts[0];
                    const sizeKb = parseFloat(parts[1]);
                    const loadTimeMs = parseInt(parts[2], 10);
                    console.log(`[ProjectMenuHandlers] Extracted values: url=${url}, sizeKb=${sizeKb}, loadTimeMs=${loadTimeMs}`);
                    if (url && !isNaN(sizeKb) && !isNaN(loadTimeMs)) {
                        scripts.push({ url, sizeKb, loadTimeMs });
                        console.log(`[ProjectMenuHandlers] Successfully added script: ${url}. Current scripts.length: ${scripts.length}`);
                    } else {
                        console.warn(`[ProjectMenuHandlers] Failed to parse script line or invalid data: url=${url}, sizeKb=${sizeKb}, loadTimeMs=${loadTimeMs}`);
                    }
                 } else {
                    console.warn(`[ProjectMenuHandlers] Skipping script line due to insufficient parts (< 3): "${line}"`, parts);
                 }
            } else if (parsingSection === 'identities') {
                 console.log(`[ProjectMenuHandlers] In 'identities' section. Processing line: "${line}"`);
                 if (parts[0] === 'Name' && parts[1] === 'Provider Name' && parts[2] === 'Value') {
                    console.log(`[ProjectMenuHandlers] Identified identity header row.`);
                 } else if (parts.length >= 3) {
                     const name = parts[0];
                     const providerName = parts[1];
                     if (name && providerName) {
                         const fullValue = parts.slice(2).join(',');
                         identities.push({ name, providerName, value: fullValue });
                     }
                }
            }
        }

        if (!websiteUrl) throw new Error("Website URL not found in CSV.");

        // --- Check and load integrations if needed ---
        let currentIntegrations = integrations;
        if (!currentIntegrations || currentIntegrations.length === 0) {
          showNotification('info', 'Loading integrations data...');
          try {
            const loadedIntegrations = await loadIntegrations(); // Attempt to load and capture the result
            // Check the returned value directly
            if (!loadedIntegrations || loadedIntegrations.length === 0) {
              // If the returned value is empty, loading failed or returned no data.
              throw new Error("Integrations still not loaded after loading attempt.");
            }
            // Use the directly returned integrations
            currentIntegrations = loadedIntegrations;
            showNotification('success', 'Integrations loaded.');
          } catch (loadError) {
            console.error("Failed to load integrations for CSV import:", loadError);
             showNotification("error", "Failed to load required integrations data. Cannot import CSV.");
             return; // Stop execution if integrations cannot be loaded
          }
        }
        // --- End integration check ---


        const nodes: FlowNode[] = [];
        const edges: FlowEdge[] = [];
        let currentX = 100;
        const ySource = 200;
        const yCluster = 200;
        const yDest = 100;
        const xSpacing = 250;
        const ySpacingDest = 80;

        const syncInjectorProviders = new Set(identities.map(id => id.providerName.toLowerCase()));
        const syncIntegrationIds = integrations
            .filter(int => int.sync_injector && syncInjectorProviders.has(int.name.toLowerCase()))
            .map(int => int.id);

        const syncInjectorNode: FlowNode = {
            id: generateNodeId('sync-injector'), type: 'sync-injector', width: 160, height: 90, position: { x: currentX, y: ySource },
            data: { type: 'sync-injector', label: 'Sync Injector', idSyncs: syncIntegrationIds, crossDomain: false, customLabelVisibility: 'visible', idSyncsVisibility: 'visible', crossDomainVisibility: 'visible' },
        };
        nodes.push(syncInjectorNode);
        currentX += xSpacing;

        const suggestedConsentToolValue = suggestConsentTool(scripts);
        const jsSourceNode: FlowNode = {
            id: generateNodeId('javascript-source'), type: 'javascript-source', width: 170, height: 70, position: { x: currentX, y: ySource },
            data: { url: websiteUrl, type: 'source', label: 'Website', subType: 'javascript', eventSpec: 'analytics-js', consentTool: suggestedConsentToolValue, eventVolume: '', latencyImpact: 250, urlVisibility: 'visible', eventSpecVisibility: 'hidden', otherConsentToolName: '', consentToolVisibility: 'visible', eventVolumeVisibility: 'visible', latencyImpactVisibility: 'visible' },
        };
        nodes.push(jsSourceNode);
        currentX += xSpacing;

        const clusterNode: FlowNode = {
            id: generateNodeId('cluster'), type: 'cluster', width: 240, height: 115, position: { x: currentX, y: yCluster },
            data: { type: 'cluster', label: DEFAULT_CLUSTER_NAME, modules: Object.entries(CLUSTER_SETTINGS).map(([moduleId, enabled]) => ({ moduleId, enabled })), teamAccess: CLUSTER_TEAM_ACCESS, clusterEnvironment: { name: "MetaRouter Managed", hintText: "Best for most use cases", environmentId: "mr-managed" }, customLabel: '', otherTeamAccess: '', moduleVisibility: 'visible', totalLatencyImpact: 0, teamAccessVisibility: 'hidden', customLabelVisibility: 'visible', environmentVisibility: 'visible' },
        };
        nodes.push(clusterNode);
        currentX += xSpacing;

        let destY = yDest;
        let unknownY = ySource + 100 + 128;
        const addedDestIntegrations = new Set<string>();
        const destStartX = currentX + 128;

        console.log(`[ProjectMenuHandlers] Before destination loop: scripts.length = ${scripts.length}, integrations.length = ${integrations.length}`);
        if (scripts.length > 0) console.log("[ProjectMenuHandlers] First script URL:", scripts[0].url);
        if (integrations.length > 0) console.log("[ProjectMenuHandlers] First integration name:", integrations[0].name);

        console.log("[ProjectMenuHandlers] Starting destination/unknown node creation loop...");
        for (const script of scripts) {
            console.log(`[ProjectMenuHandlers] Processing script URL: ${script.url}`);
            // Use currentIntegrations which is guaranteed to be loaded here
            const integration = findIntegrationForUrl(script.url, currentIntegrations);
            console.log(`[ProjectMenuHandlers] findIntegrationForUrl result: ${integration ? `"${integration.name}" (Destination: ${integration.destination})` : 'null'}`);

            if (integration && integration.destination) {
                if (!addedDestIntegrations.has(integration.id)) {
                    console.log(`[ProjectMenuHandlers] Creating destination node for: ${integration.name}`);
                    // Use loadTimeMs from CSV as latencyImpact, fallback to 0 if invalid
                    const latencyFromAudit = !isNaN(script.loadTimeMs) ? script.loadTimeMs : 0;
                    const destNode: FlowNode = {
                        id: generateNodeId('destination'), type: 'destination', width: 180, height: 60, position: { x: destStartX, y: destY },
                        // Use latencyFromAudit for latencyImpact, only fileSize for customLabel
                        data: { docs: integration.documentation || { links: [] }, icon: integration.logo_url || '', name: integration.name, type: 'destination', label: integration.name, status: '', category: integration.category || '', customLabel: `${formatFileSize(script.sizeKb)}`, description: integration.description || '', latencyImpact: latencyFromAudit, statusVisibility: 'visible', customLabelVisibility: 'visible', integrationId: integration.id },
                    };
                    nodes.push(destNode);
                    edges.push({ id: generateEdgeId(clusterNode.id, destNode.id), type: 'custom', source: clusterNode.id, target: destNode.id, animated: false });
                    addedDestIntegrations.add(integration.id);
                    destY += ySpacingDest;
                } else {
                     console.log(`[ProjectMenuHandlers] Destination node for "${integration.name}" already added, skipping duplicate.`);
                }
            } else if (!integration) {
                console.log(`[ProjectMenuHandlers] No specific integration found. Attempting to use 'Custom' destination for: ${script.url}`);
                 // Use currentIntegrations
                const customIntegration = currentIntegrations.find(int => int.name.toLowerCase() === 'custom' && int.destination);

                if (customIntegration) {
                    console.log(`[ProjectMenuHandlers] Found 'Custom' destination (ID: ${customIntegration.id}). Creating node.`);
                    // Use loadTimeMs from CSV as latencyImpact, fallback to 0 if invalid
                    const latencyFromAudit = !isNaN(script.loadTimeMs) ? script.loadTimeMs : 0;
                    const unknownDestNode: FlowNode = {
                        id: generateNodeId('custom-dest'), type: 'destination', width: 180, height: 60, position: { x: currentX - xSpacing, y: unknownY },
                         // Use latencyFromAudit for latencyImpact, only fileSize for customLabel
                        data: { docs: customIntegration.documentation || { links: [] }, icon: customIntegration.logo_url || '', name: customIntegration.name, type: 'destination', label: `${script.url.split('/').pop() || script.url}`, status: '', category: customIntegration.category || 'Custom', customLabel: `${formatFileSize(script.sizeKb)}`, description: `Represents unmapped script: ${script.url}`, latencyImpact: latencyFromAudit, statusVisibility: 'visible', customLabelVisibility: 'visible', integrationId: customIntegration.id },
                    };
                    nodes.push(unknownDestNode);
                    edges.push({ id: generateEdgeId(jsSourceNode.id, unknownDestNode.id), type: 'custom', source: jsSourceNode.id, target: unknownDestNode.id, targetHandle: null, animated: false });
                    unknownY += ySpacingDest;
                } else {
                    console.warn(`[ProjectMenuHandlers] Could not find a 'Custom' destination integration. Skipping node creation for unknown script: ${script.url}`);
                }
            } else {
                console.log(`[ProjectMenuHandlers] Script "${script.url}" matched integration "${integration.name}", but it's not a destination. Skipping node creation.`);
            }
        }

        edges.push({ id: generateEdgeId(syncInjectorNode.id, jsSourceNode.id), type: 'custom', source: syncInjectorNode.id, target: jsSourceNode.id, animated: false });
        edges.push({ id: generateEdgeId(jsSourceNode.id, clusterNode.id), type: 'custom', source: jsSourceNode.id, target: clusterNode.id, animated: false });

        console.log(`[ProjectMenuHandlers] Calling loadLayoutFromCsv with ${nodes.length} nodes and ${edges.length} edges.`);
        useStore.getState().loadLayoutFromCsv(nodes, edges);

        showNotification("success", "Audit CSV layout loaded successfully");

    } catch (error: any) {
        console.error("Error importing Audit CSV:", error);
        showNotification("error", `Failed to import Audit CSV: ${error.message}`);
    } finally {
        if (csvFileInputRef.current) csvFileInputRef.current.value = '';
    }
  };

  const handleClearCanvas = useCallback(() => {
    // Clear the canvas
    setNodes([]);
    setEdges([]);

    // Clear local storage items
    localStorage.removeItem('mr-architect-flow-state');
    reportService.clearReportFromLocalStorage();

    showNotification("success", "Canvas cleared");
    setIsOpen(false);
  }, [setNodes, setEdges, showNotification, setIsOpen]);


  return {
    handleSave,
    handleSaveAs,
    handleDelete,
    handleNewProject, // This now triggers the check/dialog
    proceedWithNewProject, // Exported for dialog confirm
    saveAndProceedWithNewProject, // Exported for dialog save option
    handleTemplateSelect,
    handleExportProject,
    handleImportProject,
    handleImportAuditCsv,
    handleFileChange,
    handleCsvFileChange,
    handleClearCanvas, // Added clear canvas handler
  };
}
